import { Camera } from 'lucide-react';
import type { ChecklistRespostaValor, ChecklistItemSchema } from '@portal-leo/shared';

interface ChecklistFillItemProps {
  item: ChecklistItemSchema & { id: string; dataPrazo?: string | null };
  value: ChecklistRespostaValor | undefined;
  onChange: (value: ChecklistRespostaValor) => void;
  disabled?: boolean;
}

function getPrazoColor(dataPrazo: string, isFilled: boolean): string {
  if (isFilled) return 'text-gray-500';
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const prazo = new Date(dataPrazo + 'T00:00:00');

  if (prazo < now) return 'text-red-600';
  return 'text-gray-500';
}

function formatDate(dateStr: string): string {
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
}

export function ChecklistFillItem({ item, value, onChange, disabled }: ChecklistFillItemProps) {
  const currentValue = value?.valor;

  const handleChange = (newValue: boolean | string | number | null) => {
    onChange({
      valor: newValue,
      observacao: value?.observacao,
    });
  };

  return (
    <div className="flex items-start gap-3 py-2">
      <div className="flex-1">
        <p className="text-sm font-medium text-gray-900">{item.nome}</p>
        {item.descricao && <p className="text-xs text-gray-500 mt-0.5">{item.descricao}</p>}
        {item.dataPrazo && (
          <p className={`text-xs mt-0.5 ${getPrazoColor(item.dataPrazo, currentValue !== null && currentValue !== undefined)}`}>
            Prazo: {formatDate(item.dataPrazo)}
          </p>
        )}
      </div>

      <div className="flex-shrink-0">
        {item.tipo === 'CHECKBOX' && (
          <input
            type="checkbox"
            checked={!!currentValue}
            onChange={(e) => handleChange(e.target.checked ? true : null)}
            disabled={disabled}
            className={`h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          />
        )}

        {item.tipo === 'STATUS' && (
          <div className="flex flex-wrap gap-2">
            {(item.config as any)?.options?.map((option: string) => (
              <label
                key={option}
                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs border ${
                  disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                } ${
                  currentValue === option
                    ? 'bg-primary-100 border-primary-500 text-primary-700'
                    : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                }`}
              >
                <input
                  type="radio"
                  name={`item-${item.id}`}
                  value={option}
                  checked={currentValue === option}
                  onChange={() => handleChange(option)}
                  disabled={disabled}
                  className="sr-only"
                />
                {option}
              </label>
            ))}
          </div>
        )}

        {item.tipo === 'TEXTO' && (
          <input
            type="text"
            value={(currentValue as string) || ''}
            onChange={(e) => handleChange(e.target.value)}
            disabled={disabled}
            placeholder="Digite..."
            className={`px-2 py-1 text-sm border border-gray-200 rounded focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 w-48 ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : ''}`}
          />
        )}

        {item.tipo === 'NUMERO' && (
          <input
            type="number"
            value={(currentValue as number) || ''}
            onChange={(e) => handleChange(e.target.value ? Number(e.target.value) : null)}
            disabled={disabled}
            placeholder="0"
            min={(item.config as any)?.min}
            max={(item.config as any)?.max}
            className={`px-2 py-1 text-sm border border-gray-200 rounded focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 w-24 ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : ''}`}
          />
        )}

        {item.tipo === 'FOTO' && (
          <button
            type="button"
            disabled={disabled}
            className={`flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-200 rounded ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}`}
          >
            <Camera className="h-4 w-4 text-gray-500" />
            {Array.isArray(currentValue) && currentValue.length > 0
              ? `${currentValue.length} foto(s)`
              : 'Adicionar'}
          </button>
        )}
      </div>
    </div>
  );
}
